/**
 * Private route component for protected routes
 * @module features/auth/components/PrivateRoute
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface PrivateRouteProps {
  children: React.ReactNode;
  requiredRole?: string | string[];
}

export const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, requiredRole }) => {
  const { isAuthenticated, isAuthLoading, role, extraRoles } = useAuth();
  const location = useLocation();

  if (isAuthLoading) {
    return null;
  }

  if (!isAuthenticated) {
    // Remember where the visitor was going (a renewal link from DAT, a
    // notification) so the login form sends them back there.
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  if (requiredRole) {
    const allowed = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    const hasAccess = (role && allowed.includes(role)) || extraRoles.some(er => allowed.includes(er));
    if (!hasAccess) {
      // "/" itself redirects to /dashboard, so sending a role-less
      // authenticated user there would loop forever. Student portal
      // accepts any authenticated user.
      return <Navigate to="/student-portal" replace />;
    }
  }

  return <>{children}</>;
};

export default PrivateRoute;
